import {
  createSlice,
  PayloadAction,
} from '@reduxjs/toolkit';

export interface EnrichmentCompanyData {
  category: {
    industry: string;
    industryGroup: string;
    naicsCode: string;
    sector: string;
    sicCode: string;
    subIndustry: string;
  }
  crunchbase: {
    handle: null;
  }
  description: string;
  domain: string;
  domainAliases: string[];
  emailProvider: boolean;
  facebook: {
    likes: number;
    handle: number;
  }
  foundedYear: number;
  geo: {
    city: string;
    country: string;
    countryCode: string;
    lat: number;
    lng: number;
    postalCode: number;
    state: string;
    stateCode: string;
    streetName: string;
    streetNumber: number;
    subPremise: string;
  }
  id: string;
  identifiers: {
    usEIN: string;
  }
  indexedAt: string;
  legalName: string;
  linkedin: {
    handle: string;
  }
  location: string;
  logo: string;
  metrics: {
    alexaGlobalRank: number;
    alexaUsRank: string;
    annualRevenue: string;
    employees: number;
    employeesRange: string;
    estimatedAnnualRevenue: string;
    fiscalYearEnd: string;
    marketCap: string;
    raised: number;
  }
  name: string;
  parent: {
    domain: string;
  }
  phone: string;
  site: {
    phoneNumbers: string[];
    emailAddresses: string[];
  }
  tags: string[];
  tech: string[];
  techCategories: string[];
  ticker: string;
  timeZone: string;
  twitter: {
    avatar: string;
    bio: string;
    followers: number;
    following: number;
    handle: string;
    id: string;
    location: string;
    site: string;
  }
  type: string;
  ultimateParent: {
    domain: string;
  }
  utcOffset: string;
}

export interface EnrichmentPersonData {
  avatar: string;
  bio: string;
  email: string;
  emailProvider: boolean;
  employment: {
    domain: string;
    name: string;
    role: string;
    seniority: string;
    subRole: string;
    title: string;
  };
  facebook: {
    handle: string;
  }
  fuzzy: boolean;
  geo: {
    city: string;
    country: string;
    countryCode: string;
    lat: number;
    lng: number;
    state: string;
    stateCode: string;
  }
  github: {
    avatar: string;
    blog: string;
    company: string;
    followers: number;
    following: number;
    handle: string;
    id: string;
  }
  googleplus: {
    handle: string;
  }
  gravatar: {
    avatar: string;
    avatars: string[];
    handle: string;
    urls: string | string[];
  }
  id: string;
  indexedAt: string;
  linkedin: {
    handle: string;
  }
  location: string;
  name: {
    givenName: string;
    familyName: string;
    fullName: string;
  }
  site: string;
  timeZone: string;
  twitter: {
    avatar: string;
    bio: string;
    favorites: string;
    followers: number;
    following: number;
    handle: string;
    id: string;
    location: string;
    site: string;
    statuses: string;
  }
  utcOffset: number;
}

export interface EMSAttendee {
  name: string;
  uuid: string;
  tag: string
  desc: string;
  company: string;
}

export interface EnrichmentData {
  company: EnrichmentCompanyData;
  person: EnrichmentPersonData;
  attendee: EMSAttendee;
}

export const selectEnrichmentDataShow = (s) => s.enrichmentData.enrichmentDataShow;
export const selectattendeeEnrichmentData = (s) => s.enrichmentData.attendeeEnrichmentData as EnrichmentData;

export const enrichmentDataSlice = createSlice({
  name: 'enrichmentData',
  initialState: {
    enrichmentDataShow: false,
    attendeeEnrichmentData: null,
  },
  reducers: {
    setEnrichmentDataShow: (state, action: PayloadAction<boolean>) => {
      state.enrichmentDataShow = action.payload;
    },
    setEnrichmentData: (state, action: PayloadAction<EnrichmentData>) => {
      state.attendeeEnrichmentData = action.payload;
    },
    resetEnrichmentData: (state) => {
      state.attendeeEnrichmentData = null;
    },
  },
});

export const { setEnrichmentDataShow, setEnrichmentData, resetEnrichmentData } = enrichmentDataSlice.actions;

export default enrichmentDataSlice.reducer;
